import React, { useEffect, useState } from "react";
import MembershipCard, { MembershipCardProps } from "./membership-card";
import { useNavigate } from "react-router-dom";
import { CaretRight, CaretUp } from "../../../icons";
import BecomeMemberButton from "../../common/membership/BecomeMemberButton";
// import { getLocalStorage } from "../../../helpers/storage";
import { useLazyGetUserSubscriptionQuery } from "../../../store/api/userApi";
import { RightIcon, WrongIcon } from "../../../icons/tags-icons";

interface BenefitItemProps {
  icon: string;
  title: string;
  description: string;
}

const BenefitItem: React.FC<BenefitItemProps> = ({
  icon,
  title,
  description,
}) => (
  <div className="flex items-center items-start gap-4 p-4 rounded-lg transition-colors">
    <img src={icon} alt={title} className="w-10 h-10" />

    <div>
      <h3 className="font-semibold text-white text-[24px]">{title}</h3>
      <p className="text-[15px] text-[#CDCDCD]">{description}</p>
    </div>
  </div>
);

const MembershipPage = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = React.useState<number | null>(null);
  // const user = JSON.parse(getLocalStorage("userData") || "{}");
  const [userSubscription, setUserSubscription] = React.useState<
    MembershipCardProps[]
  >([]);
  const [getUserSubscription] = useLazyGetUserSubscriptionQuery();
  const [isCourse, setIsCourse] = useState(false);
  const [isYearly, setIsYearly] = useState(false);

  const benefits = [
    {
      icon: "/assets/membership/notebook-dynamic-premium.png",
      title: "Money School",
      description:
        "Learn from top creators: all you need to know about personal finance, stocks, insurance and credit cards.",
      isYearly: true,
      isInModular: true,
    },
    {
      icon: "/assets/membership/copy-dynamic-premium.png",
      title: "Bootcamp",
      description:
        "A tailored, gamified learning experience guided by a mentor who gets you.",
      isYearly: false,
      isInModular: true,
    },
    {
      icon: "/assets/membership/tool-dynamic-premium.png",
      title: "Financial Tools",
      description:
        "Tailor your path to financial freedom by using our proprietary financial planning tools.",
      isYearly: true,
      isInModular: false,
    },
    {
      icon: "/assets/membership/money-bag-dynamic-premium.png",
      title: "Save Everyday",
      description: "Enjoy exclusive instant discounts on 400+ brands.",
      isYearly: true,
      isInModular: false,
    },
    {
      icon: "/assets/membership/calender-dynamic-premium.png",
      title: "1% Experiences",
      description:
        "Attend curated offline events and build your network by meeting amazing people.",
      isYearly: true,
      isInModular: false,
    },
    {
      icon: "/assets/membership/play-dynamic-premium.png",
      title: "Livestreams",
      description:
        "Join our weekly livestreams to stay updated and learn more with the 1% Club.",
      isYearly: true,
      isInModular: false,
    },
  ];

  const faqs = [
    {
      question:
        "How does The 1% Club stand out from the typical online course?",
      answer: (
        <div className="space-y-6 text-[#E1E1E1]">
          <p>
            The term course has been somewhat tarnished, particularly in the
            realm of online education. Many online courses often fall short in
            delivering tangible results. Our approach seeks to create a
            comprehensive learning experience that combines the best elements of
            both online and traditional education.
          </p>
          <ul className="space-y-4 list-disc list-inside pl-4">
            <li>
              <span className="font-semibold">Bootcamp:</span> Weekly live
              sessions with experts and industry leaders for an interactive,
              lecture-style learning experience.
            </li>
            <li>
              <span className="font-semibold">Online Learning Program:</span> A
              digital library of high-quality episodes, covering everything from
              basics to advanced topics.
            </li>
            <li>
              <span className="font-semibold">Peer-to-peer Learning:</span>{" "}
              Community Q&A for insightful discussions and support.
            </li>
          </ul>
          <p>
            We've also added gamification—badges, XP points, and rewards—to keep
            you motivated on your learning journey!
          </p>
        </div>
      ),
    },
    {
      question: "Are there going to be more episodes?",
      answer: (
        <div className="space-y-6 text-[#E1E1E1]">
          <p>
            We are constantly upgrading the learning modules with in depth
            content based on market changes and more.
          </p>
          <p>
            These episodes will be made available to you at no additional cost!
          </p>
        </div>
      ),
    },
    {
      question:
        "What if I do not have enough time for the Bootcamp? Is it compulsory?",
      answer: (
        <div className="space-y-6 text-[#E1E1E1]">
          <p>
            We completely understand that you have your college or job or family
            to juggle. How else would you earn the money that you are looking to
            grow?
          </p>
          <p>
            While you have the flexibility to complete the modules at your own
            pace with lifetime access, having daily targets and the support of a
            mentor can significantly boost your motivation and progress.
          </p>
          <p>
            Moreover, your learning journey within The 1% Club becomes even more
            rewarding after you successfully graduate from the Money School, as
            it marks the crucial first step toward your financial empowerment.
          </p>
        </div>
      ),
    },
    {
      question: "How can I attend The 1% Club offline events?",
      answer: (
        <div className="space-y-6 text-[#E1E1E1]">
          <p>
            Currently, we have a waitlist. Once you clear the waitlist, you will
            be given the option to choose between the programs. Participants can
            select one of the programs or opt for both, depending on their
            preferences and financial goals.
          </p>
        </div>
      ),
    },
  ];

  const fetchUserSubscription = async () => {
    try {
      const response = await getUserSubscription()?.unwrap();
      if (response?.success) {
        setUserSubscription(response.data);
        console.log(response.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchUserSubscription();
  }, []);

  useEffect(() => {
    if (userSubscription.length === 0) return;
    if (userSubscription[0].subscription_type === "course") {
      setIsCourse(true);
    }
    if (userSubscription[0].validity === "yearly") {
      setIsYearly(true);
    }
  }, [userSubscription]);

  return (
    <div className="min-h-screen bg-black p-8">
      <div className="max-w-3xl mx-auto relative">
        <div className="flex items-center gap-2 text-white mb-8">
          <div
            className="cursor-pointer rotate-180"
            onClick={() => navigate("/home")}
          >
            <CaretRight />
          </div>

          <h1 className="text-[34px] font-bold">Membership</h1>
        </div>
        {userSubscription?.length > 0 ? (
          <MembershipCard userSubscription={userSubscription} />
        ) : (
          <>
            <div className="mb-10 mt-10">
              <div className="flex flex-col font-bold text-[28px] w-1/2">
                <div className="bg-gradient-text text-transparent bg-clip-text">
                  Become a Lifetime
                </div>
                <div className="bg-gradient-text text-transparent bg-clip-text">
                  Member of the 1% Club
                </div>
                <BecomeMemberButton
                  text="Join the Club"
                  buttonCustomClass="w-3/4 mb-5 px-4 py-2 text-base rounded-full mt-4 bg-secondary-300 hover:bg-blue-600 text-white"
                />
              </div>
              <div className="mt-[10px] absolute right-[-80px] top-[-100px]">
                <img
                  src="/assets/membership/one-percent.png"
                  alt="membership"
                  className="w-[500px] h-[500px]"
                />
              </div>
            </div>
          </>
        )}

        {isCourse ? (
          <div>
            <h2 className="text-[28px] font-bold text-white mb-6 mt-6">
              Membership Benefits
            </h2>
            <div className="w-full">
              <div className="w-full flex justify-between text-white h-[70px] items-center">
                <h1 className="text-white text-[20px] w-[310px] bg-[#262626] h-full flex items-center pl-4 border-t-[1px] border-x-[1px] border-[#404040] rounded-t-[10px]">
                  Offerings
                </h1>
                <div className="flex flex-1 justify-evenly text-[20px]">
                  <h2 className="w-[200px] text-right">Individual</h2>
                  <h2 className="w-[200px] text-right">Membership</h2>
                </div>
              </div>
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className={`w-full flex justify-between text-white border-t-[1px] border-[#404040] h-[70px] items-center`}
                >
                  <h1
                    className={`text-white w-[310px] bg-[#262626] h-full flex items-center pl-4 border-x-[1px] border-[#404040] ${index === benefits.length - 1 ? "rounded-b-[10px] border-b-[1px]" : ""}`}
                  >
                    <img
                      src={benefit.icon}
                      alt={benefit.title}
                      className="w-[32px] h-[32px] inline-block mr-2"
                    />
                    <div className="text-white">{benefit.title}</div>
                  </h1>
                  <div className="flex flex-1 justify-evenly">
                    <h2 className="w-[200px] flex justify-end">
                      {benefit.isInModular ? <RightIcon /> : <WrongIcon />}
                    </h2>
                    <h2 className="w-[200px] flex justify-end">
                      <RightIcon />
                    </h2>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-10">
            <h2 className="text-[28px] font-bold text-white mb-6">
              {isYearly
                ? "Membership Benefits for 1 year"
                : "Membership Benefits"}
            </h2>
            <div className="bg-[#2C2C2E] border-[1px] border-[#404040] rounded-[10px]">
              <div className="grid gap-4 divide-y divide-[#404040]">
                {benefits.map((benefit, index) => (
                  <>
                    {benefit.isYearly && (
                      <div>
                        <BenefitItem key={index} {...benefit} />
                      </div>
                    )}
                  </>
                ))}
              </div>
            </div>

            {isYearly && (
              <>
                <h2 className="text-[28px] font-bold text-white my-6">
                  Membership Benefits Not Included
                </h2>
                <div className="bg-[#2C2C2E] border-[1px] border-[#404040] rounded-[10px]">
                  <div className="grid gap-4 divide-y divide-[#404040]">
                    <div>
                      <BenefitItem {...benefits[1]} />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div className="mt-6">
          <h2 className="text-[28px] font-bold text-white mb-6">FAQs</h2>
          <div className="bg-[#2C2C2E] border-[1px] border-[#404040] rounded-lg divide-y divide-[#404040]">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="cursor-pointer"
                onClick={() =>
                  setExpandedFaq(expandedFaq === index ? null : index)
                }
              >
                <div className="flex justify-between items-center p-4 ">
                  <p className="text-white">{faq.question}</p>
                  <div
                    className={`w-5 h-5 text-white transition-transform ${expandedFaq === index ? "" : "rotate-180"}`}
                  >
                    <CaretUp width={20} height={20} strokeColor="white" />
                  </div>
                </div>
                {expandedFaq === index && (
                  <div className="p-4 pt-0 text-zinc-400">{faq.answer}</div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="mt-6">
          <div className="bg-[#2C2C2E] border-[1px] border-[#404040] rounded-[10px] p-4 text-[#999999] text-[17px] underline cursor-pointer">
            <a
              href="https://www.onepercentclub.io/terms-conditions"
              target="_blank"
              rel="noopener noreferrer"
            >
              View Terms & Conditions
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipPage;
